/**
 * Configuration for iframe embed authentication.
 *
 * This config manages which origins are allowed to embed the Primepoint app
 * and receive authentication tokens via postMessage.
 */

/**
 * List of allowed origins for postMessage authentication.
 *
 * Security: Only origins in this list can receive session tokens from the auth popup.
 *
 * Origins include:
 * - Same origin (always allowed)
 * - Local development ports for testing cross-origin embeds
 * - Production Procore domains
 * - Procore sandbox/staging domains
 */
const normalizeOrigin = (value: string | undefined): string | undefined => {
    if (!value) {
        return undefined;
    }

    try {
        return new URL(value).origin;
    } catch {
        return value.replace(/\/+$/, "");
    }
};

/**
 * Route of the popup page that runs the Stytch discovery flow and hands
 * session tokens to the embedding iframe via postMessage.
 */
export const EMBED_AUTH_PATH = "/embed-auth";

// The client's build (vite.config.mjs) defines `process.env.*`; another bundler of this
// shared module may not define `process` at all, and a bare read throws during the first
// import. Guard the global so every bundler gets the fallback.
const BUILD_ENV: Record<string, string | undefined> =
    typeof process !== "undefined" && process.env ? process.env : {};
const LOCAL_API_ORIGIN =
    normalizeOrigin(BUILD_ENV.REACT_APP_API_BASE_URL) ||
    `http://localhost:${BUILD_ENV.REACT_APP_API_PORT || "3001"}`;

export const ALLOWED_EMBED_ORIGINS = [
    // Same origin always allowed
    typeof window !== "undefined" ? window.location.origin : "",

    // Local development
    "http://localhost:8000",
    LOCAL_API_ORIGIN,

    // Procore production (no trailing slashes - event.origin never has them)
    "https://app.procore.com",

    // Procore sandbox/staging
    "https://sandbox.procore.com",
    "https://login-sandbox.procore.com",

    // Procore regional instances
    "https://us02.procore.com",
    "https://uk01.procore.com",

    // Add other trusted origins as needed
].filter(Boolean); // Remove empty string if window is undefined

/**
 * Bottom padding for embedded content to prevent cutoff by Procore footer.
 * Accounts for iOS safe area insets.
 */
export const EMBED_BOTTOM_PADDING_PX = 72;

export const EMBED_BOTTOM_PADDING = `max(${EMBED_BOTTOM_PADDING_PX}px, calc(${EMBED_BOTTOM_PADDING_PX}px + env(safe-area-inset-bottom)))`;
